import { keys } from "@/lib/utils/keys";
import { routes } from "@/lib/utils/routes";
import { constants } from "@/lib/utils/constants";
import { logError } from "@/lib/utils/errors/logger";
import { getStoreRating } from "@/lib/services/general";
import { META_INFO, TITLE } from "@/lib/utils/helpers/meta_info";
import { getBlogPageMeta, getCMSPageMeta } from "@/lib/services/cmsPage";
import { getCategoryMeta, getProductMeta } from "@/lib/services/pageMeta";
import {
  getHomePage,
  getOrganizationInfo,
  getContentfulImgData,
} from "@/lib/services/contentful";

const capitalizeFirstLetter = (str) => {
  return str
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

const replaceString = (baseStr, searchVal, newVal) => {
  return baseStr.replaceAll(searchVal, newVal);
};

const formatToInternationalUKPhoneNumber = (phoneNumber) => {
  try {
    // Remove any spaces for consistency
    const cleaned = phoneNumber?.replace(/\s+/g, "");

    // If the number is already in international format with +44
    if (/^\+44\d{10}$/.test(cleaned)) {
      // Add spaces for readability
      return cleaned?.replace(/^\+44(\d{3})(\d{3})(\d{4})$/, "+44 $1 $2 $3");
    }

    // If the number starts with 0, replace the leading 0 with +44 and format it
    if (/^0\d{10}$/.test(cleaned)) {
      return cleaned?.replace(/^0(\d{3})(\d{3})(\d{4})$/, "+44 $1 $2 $3");
    }
  } catch (error) {
    logError({
      error,
      info: { phoneNumber },
      message: "formatToInternationalUKPhoneNumber",
    });
  }

  // Return the original input if it doesn't match expected formats
  return phoneNumber;
};

const updateAdminMeta = (object, metaRes, page = "") => {
  // This will update meta info set by admin
  const { metadata, hasMeta } = getPageMetaByAdmin(metaRes, "name");
  if (hasMeta) {
    if (metadata?.meta_keywords) {
      object.meta_keywords = metadata.meta_keywords;
    }

    if (metadata?.meta_title) {
      object.meta_title = metadata.meta_title + page;
    }

    if (metadata?.meta_description) {
      object.meta_description = metadata.meta_description + page;
    }
  }
  return object;
};

const getPageMetaByAdmin = (resObj = {}, fieldToCheck = "") => {
  let response = { hasMeta: false, metadata: {} };
  const hasMetaInfoValue = (f) => resObj.hasOwnProperty(f) && resObj[f];

  if (fieldToCheck && resObj.hasOwnProperty(fieldToCheck)) {
    if (hasMetaInfoValue("meta_title")) {
      response.metadata["meta_title"] = resObj.meta_title;
    }

    if (hasMetaInfoValue("meta_description")) {
      response.metadata["meta_description"] = resObj.meta_description;
    }

    if (hasMetaInfoValue("meta_keywords")) {
      response.metadata["meta_keywords"] = resObj.meta_keywords;
    }
    response.hasMeta = Object.keys(response.metadata).length > 0;
  }
  return response;
};

const getMetaImages = async (altText) => {
  try {
    const { data } = await getHomePage();
    if (data?.items?.length > 0 && data.items[0]?.fields?.seo?.fields) {
      const { imageUrl } = getContentfulImgData(
        data.items[0]?.fields?.seo?.fields.shareImage,
      );

      if (imageUrl) {
        return {
          images: {
            url: imageUrl,
            alt: altText,
          },
        };
      }
    }
  } catch (error) {
    logError({ error, info: { altText }, message: "getMetaImages" });
  }
  return {};
};

const updateMetaObject = (metaObj, res, url, shareImages) => {
  metaObj.title = res?.meta_title || metaObj?.title;
  metaObj.description = res?.meta_description || "";
  metaObj.keywords = res?.meta_keywords || res?.meta_keyword || "";
  metaObj.openGraph = {
    url,
    title: metaObj.title,
    description: metaObj?.description,
    ...shareImages,
  };

  return metaObj;
};

const updateProductMeta = async (productSKU, metaData, currentURL) => {
  const { data, errors } = await getProductMeta({ productSKU });
  if (errors) {
    const isArray = Array.isArray(errors);
    logError({
      error: isArray ? JSON.stringify(errors) : errors,
      info: { sku: productSKU, currentURL },
      message: "updateProductMeta",
    });
  } else {
    const metaRes = data?.products?.items?.[0] || {};
    if (metaRes?.name) {
      let itemImg = {};
      const metaRes = data?.products?.items?.[0];
      const brand = metaRes?.extraVariable?.brand_info || {};
      let title = metaRes?.name;
      if (brand?.title) {
        title = `${brand.title.toUpperCase()} ${title}`;
      }

      const itemMeta = { ...META_INFO.product }; // Static template provided by SEO team (IN)
      let metaObj = {
        meta_title: itemMeta.meta_title.replace("{{ TITLE }}", title),
        meta_description: itemMeta.meta_description.replace(
          "{{ TITLE }}",
          title,
        ),
      };

      metaObj = updateAdminMeta(metaObj, metaRes);
      if (metaRes?.thumbnail?.url) {
        itemImg = {
          images: {
            url: metaRes.thumbnail.url,
            alt: metaRes.thumbnail?.label || title,
          },
        };
      }
      metaData = updateMetaObject(metaData, metaObj, currentURL, itemImg);
    }
  }
  return metaData;
};

const updateCategoryMeta = async (catId, metaData, currentURL, pageNo) => {
  const { data, errors } = await getCategoryMeta({ catId });
  if (errors) {
    logError({
      error: errors,
      info: { catId, currentURL, pageNo },
      message: "updateCategoryMeta",
    });
  } else {
    const metaRes = data?.categoryList?.[0] || {};
    if (metaRes?.name) {
      let shareImages = {};
      const catName = metaRes?.name;
      const pageInfo = pageNo && Number(pageNo) > 1 ? ` - Page ${pageNo}` : "";
      let templateData = { ...META_INFO.category }; // Static template provided by SEO team (IN)
      let metaObj = {
        meta_title: templateData.meta_title.replace(
          "{{ CATEGORY }}",
          `${catName + pageInfo}`,
        ),
        meta_description: templateData.meta_description.replaceAll(
          "{{ CATEGORY }}",
          catName,
        ),
      };
      metaObj.meta_description += `${pageInfo}`;
      metaObj = updateAdminMeta(metaObj, metaRes, pageInfo);

      if (metaObj?.image) {
        shareImages = {
          images: { alt: catName, url: metaObj.image },
        };
      } else {
        shareImages = await getMetaImages(catName);
      }

      metaData = updateMetaObject(metaData, metaObj, currentURL, shareImages);
    }
  }
  return metaData;
};

const updateBranchMeta = async (branch, metaData, currentURL) => {
  let metaRes = { ...META_INFO.branch };
  const location = capitalizeFirstLetter(branch?.location);

  metaRes.meta_title = META_INFO?.branch?.initial_meta_title;
  metaRes.meta_title = metaRes.meta_title.replace("{{ LOCATION }}", location);
  metaRes.meta_description = metaRes.meta_description.replace(
    "{{ LOCATION }}",
    location,
  );

  let shareImages = {};
  if (branch?.image?.url) {
    shareImages = {
      images: {
        url: branch?.image?.url,
        alt: branch?.image?.name,
      },
    };
  } else {
    shareImages = await getMetaImages(constants.contentTypes.branch);
  }

  return updateMetaObject(metaData, metaRes, currentURL, shareImages);
};

const getMakeAndModelsPageMeta = async ({
  type,
  metaData,
  currentURL,
  queryParams = {},
}) => {
  let altText = type;
  let metaRes = META_INFO.makeandmodels.archive;
  if (type === constants.contentTypes.modelselected) {
    const make = capitalizeFirstLetter(queryParams?.make || "");
    const model = capitalizeFirstLetter(queryParams?.model || "");

    altText = make + " " + model;
    metaRes = { ...META_INFO.makeandmodels.make_and_model_selected };
    metaRes.meta_title = metaRes.meta_title.replace("{{ MAKE }}", make);
    metaRes.meta_title = metaRes.meta_title.replace("{{ MODEL }}", model);
    metaRes.meta_description = metaRes.meta_description.replace(
      "{{ MAKE }}",
      make,
    );
    metaRes.meta_description = metaRes.meta_description.replace(
      "{{ MODEL }}",
      model,
    );
  } else if (type === constants.contentTypes.makeselected) {
    metaRes = { ...META_INFO.makeandmodels.make_selected };
    altText = capitalizeFirstLetter(queryParams?.make || "");
    metaRes.meta_title = metaRes.meta_title.replace("{{ MAKE }}", altText);
    metaRes.meta_description = metaRes.meta_description.replace(
      "{{ MAKE }}",
      altText,
    );
  }
  const shareImages = await getMetaImages(altText);
  return updateMetaObject(metaData, metaRes, currentURL, shareImages);
};

const fetchCMSPageMeta = async (metaType, data, baseMetaData, pageURL) => {
  const apiArr = [getMetaImages(metaType)];
  if (data?.relative_url) {
    apiArr.push(getCMSPageMeta(data?.relative_url));
  }

  const [imageRes, cmsRes] = await Promise.allSettled(apiArr);
  let dataRes = {};
  if (cmsRes?.value?.data?.blogPostByUrlKey) {
    dataRes = cmsRes?.value?.data?.cmsPage;
  }

  if (!dataRes?.meta_title || !dataRes?.meta_description) {
    if (data?.brand) {
      const brand = capitalizeFirstLetter(data?.brand);
      dataRes.meta_title = META_INFO.brand.meta_title.replace(
        "{{ BRAND }}",
        brand,
      );
      dataRes.meta_description = META_INFO.brand.meta_description.replace(
        "{{ BRAND }}",
        brand,
      );
    } else if (data?.supplier) {
      dataRes = { ...dataRes, ...META_INFO.supplier };
    }
  }

  return updateMetaObject(
    baseMetaData,
    dataRes,
    pageURL,
    imageRes?.value || {},
  );
};

const getRelatedMeta = async (metaType, data, baseMetaData, pageURL) => {
  let pageMetaData = { ...baseMetaData };
  if (metaType === constants.contentTypes.product) {
    if (data?.productSKU) {
      pageMetaData = await updateProductMeta(
        data.productSKU,
        baseMetaData,
        pageURL,
      );
    }
  } else if (metaType === constants.contentTypes.category) {
    pageMetaData = await updateCategoryMeta(
      data?.categoryid,
      baseMetaData,
      pageURL,
      data?.page_number,
    );
  } else if (metaType === constants.contentTypes.branches) {
    const metaImg = await getMetaImages(metaType);
    let templateMeta = {
      ...META_INFO.misc,
      meta_title: META_INFO.misc.meta_title.replace(
        "{{ HEADING }}",
        "Find a Store",
      ),
    };
    pageMetaData = updateMetaObject(
      baseMetaData,
      templateMeta,
      pageURL,
      metaImg,
    );
  } else if (metaType === constants.contentTypes.branch) {
    pageMetaData = updateBranchMeta(data, baseMetaData, pageURL);
  } else if (metaType === constants.contentTypes.blogs) {
    const [imgRes, blogRes] = await Promise.allSettled([
      getMetaImages(metaType),
      getBlogPageMeta(data.title),
    ]);

    let blogMeta = { meta_title: `${data.title} | GSF Car Parts` };
    if (blogRes?.value?.data?.blogPostByUrlKey) {
      blogMeta = blogRes?.value?.data?.blogPostByUrlKey;
      blogMeta.meta_title = `${blogMeta.title} | GSF Car Parts`;
    }
    pageMetaData = updateMetaObject(
      baseMetaData,
      blogMeta,
      pageURL,
      imgRes?.value || {},
    );
  } else if (metaType === constants.contentTypes.search_result) {
    const searchImg = await getMetaImages(data?.searchText);
    const sMeta = {
      ...META_INFO.search_result,
      meta_title: META_INFO.search_result.meta_title.replace(
        "{{ QUERY }}",
        data?.searchText,
      ),
    };
    pageMetaData = updateMetaObject(baseMetaData, sMeta, pageURL, searchImg);
  } else if (metaType === constants.contentTypes.CMS_PAGE) {
    pageMetaData = await fetchCMSPageMeta(
      metaType,
      data,
      baseMetaData,
      pageURL,
    );
  } else if (
    metaType === constants.contentTypes.bloglist ||
    metaType === constants.contentTypes.sitemap
  ) {
    const siteMapImg = await getMetaImages(constants.contentTypes.sitemap);
    const metaObj = {
      ...META_INFO.misc,
      meta_title: META_INFO.misc.meta_title.replace(
        "{{ HEADING }}",
        data.heading,
      ),
    };
    pageMetaData = updateMetaObject(baseMetaData, metaObj, pageURL, siteMapImg);
  } else if (
    metaType === constants.contentTypes.makeselected ||
    metaType === constants.contentTypes.modelselected ||
    metaType === constants.contentTypes.makeandmodels
  ) {
    pageMetaData = await getMakeAndModelsPageMeta({
      type: metaType,
      queryParams: data,
      currentURL: pageURL,
      metaData: baseMetaData,
    });
  }

  return pageMetaData;
};

export const getCurrentURL = (path, withSearchQuery = false) => {
  if (!path) return "";
  try {
    const { origin, pathname, search } = new URL(path);
    let currentURL = `${origin}${pathname}`;
    if (!path.includes(keys.general.websiteUrl)) {
      const domain = new URL(keys.general.websiteUrl);
      currentURL = `${domain.origin}${pathname}`;
    }

    if (withSearchQuery) {
      currentURL += search;
    }
    return currentURL;
  } catch (error) {
    logError({ error, info: { path }, message: "getCurrentURL" });
    return "";
  }
};

export const staticMetaInfo = ({
  pageURL,
  urlWithQuery,
  canonicalPath,
  pageHeading = "",
  query = false,
}) => {
  const enableBot = enableIndexing(urlWithQuery || canonicalPath);
  let metaData = {
    title: META_INFO.misc.meta_title,
    description: META_INFO.misc.meta_description,
    robots: {
      index: enableBot,
      follow: enableBot,
      googleBot: {
        index: enableBot,
        follow: enableBot,
      },
    },
  };

  if (pageHeading) {
    metaData.title = metaData.title.replace("{{ HEADING }}", pageHeading);
  } else {
    metaData.title =
      "Car Parts & Spares – Buy Online with Fast UK Delivery | GSF Car Parts";
  }

  let currentURL = "";
  if (canonicalPath) {
    currentURL = getCurrentURL(canonicalPath, query);
  } else if (pageURL) {
    currentURL = pageURL;
  }

  if (currentURL) {
    metaData.alternates = { canonical: currentURL };
  }
  return metaData;
};

export const enableIndexing = (url) => {
  const enableBot = Number(keys.general.enableBot) === 1;
  if (enableBot) {
    const fullUrl = getCurrentURL(url, true);
    const hasUrl = constants.no_indexed_urls.find((u) => fullUrl.includes(u));
    const hasParam = constants.no_indexed_params.find((q) =>
      fullUrl.includes(`${q}=`),
    );
    if (hasUrl === undefined && hasParam === undefined) return enableBot;
    return false;
  }
  return enableBot;
};

export const getPageMetaInfo = async ({
  type,
  urlWithQuery,
  canonicalPath,
  pageData = {},
  pageHeading = "",
}) => {
  let pageString = "";
  let currentURL = getCurrentURL(canonicalPath, true);
  if (pageData?.page_number) {
    currentURL = `${currentURL}?page=${pageData.page_number}`;
    pageString = " - Page " + pageData.page_number;
  }

  let canonicalURL = currentURL;
  if (pageData?.hasManual) {
    canonicalURL = `${currentURL}?make=${pageData.manualMake}&model=${pageData.manualModel}`;
  }

  const enableBot = enableIndexing(urlWithQuery || currentURL);

  let baseMeta = {
    ...META_INFO.misc,
    alternates: { canonical: canonicalURL },
    robots: {
      index: enableBot,
      follow: enableBot,
      googleBot: {
        index: enableBot,
        follow: enableBot,
      },
    },
  };

  if (pageHeading) {
    baseMeta.title = baseMeta.meta_title.replaceAll(
      "{{ HEADING }}",
      pageHeading,
    );
  } else {
    baseMeta.title = TITLE;
  }

  baseMeta = await getRelatedMeta(type, pageData, baseMeta, currentURL);
  if (pageString && !baseMeta?.title?.includes(pageString)) {
    baseMeta.title += pageString;
    if (baseMeta?.openGraph) baseMeta.openGraph.title += pageString;
  }
  return baseMeta;
};

export const getProductSchema = (product, canonicalPath) => {
  const brand = product.brands?.[0] || {};
  const review = brand?.review || null;
  const productURL = getCurrentURL(canonicalPath);
  const imageArr = product.media_gallery.map((m) => m?.url).filter(Boolean);
  const availability = brand?.quantity > 0;

  let schemaObj = {
    "@context": "https://schema.org/",
    "@type": "Product",
    sku: brand?.sku,
    url: productURL,
    description: product.description.html,
    image: !imageArr.length ? product.image : imageArr,
    name: brand?.title_with_brand || product.product_title,
    brand: {
      "@type": "Brand",
      name: brand?.title?.toUpperCase(),
    },
    offers: {
      "@type": "Offer",
      url: productURL,
      priceCurrency: brand?.currency,
      price: brand?.final_price?.substring(1),
      itemCondition: "https://schema.org/NewCondition",
      availability: `https://schema.org/${
        availability ? "InStock" : "OutOfStock"
      }`,
      priceSpecification: {
        priceCurrency: brand?.currency,
        price: brand?.final_price?.substring(1),
        valueAddedTaxIncluded: "https://schema.org/True",
      },
      seller: {
        "@type": "Organization",
        name: "GSF Car Parts",
      },
    },
  };

  if (review) {
    schemaObj["aggregateRating"] = {
      "@type": "AggregateRating",
      bestRating: 5,
      reviewCount: review?.num_ratings,
      ratingValue: parseFloat(parseFloat(review?.average_rating).toFixed(2)),
    };
  }
  return schemaObj;
};

export const getBreadcrumbSchema = (breadcrumbList) => {
  const schema = {
    "@context": "https://schema.org/",
    "@type": "BreadcrumbList",
    itemListElement: [],
  };

  const totalLength = breadcrumbList.length;
  breadcrumbList.forEach((item, i) => {
    let listItem = {
      "@type": "ListItem",
      position: i + 1,
      name: item.title,
    };
    if (totalLength !== listItem.position) {
      listItem.item = item.url;
    }
    schema.itemListElement.push(listItem);
  });

  return schema;
};

export const getOrganizationSchema = async ({ icon = null, branch = null }) => {
  let orgSchema = {
    "@context": "http://schema.org",
    "@type": "Organization",
    url: keys.general.websiteUrl,
    vatID: "135606620",
    name: "GSF Car Parts",
    logo: "",
    image: "",
    telephone: "+44 121 626 7971",
    email: "enquiries@gsfgroup.com",
    description: "GSF Car Parts is a leading automotive parts business.",
    contactPoint: {
      "@type": "ContactPoint",
      telephone: "+44 121 626 7971",
      email: "enquiries@gsfgroup.com",
    },
    sameAs: ["https://www.facebook.com/gsfcarparts"],
    address: {
      "@type": "PostalAddress",
      streetAddress: "Unit 105 Midpoint Park Kingsbury Road",
      addressLocality: "Sutton Coldfield",
      addressCountry: "GB",
      addressRegion: "Minworth",
      postalCode: "B76 1AF",
    },
  };

  if (branch) {
    if (branch?.hyperlink) {
      orgSchema.url += `${routes.branches}/${branch.hyperlink}`;
    }

    if (branch?.telephone) {
      const telephone = formatToInternationalUKPhoneNumber(branch.telephone);
      orgSchema.telephone = telephone;
      orgSchema.contactPoint.telephone = telephone;
    }

    if (branch?.image?.length > 0) {
      orgSchema.image = branch.image[0]?.url || orgSchema.image;
    }

    orgSchema.name += ` - ${branch?.name}`;
    orgSchema.email = branch?.email || orgSchema.email;
    orgSchema.description = branch?.description || orgSchema.description;
    orgSchema.contactPoint.email =
      branch?.email || orgSchema.contactPoint.email;
    orgSchema.address.streetAddress =
      branch?.address1 || orgSchema.address.streetAddress;
    orgSchema.address.addressLocality =
      branch?.address2 || orgSchema.address.addressLocality;
    orgSchema.address.addressRegion =
      branch?.address3 || orgSchema.address.addressRegion;
    orgSchema.address.postalCode =
      branch?.postcode || orgSchema.address.postalCode;
  } else {
    // Use Promise.allSettled to execute API calls in parallel and handle errors individually
    const [ratingRes, orgRes] = await Promise.allSettled([
      getStoreRating(),
      getOrganizationInfo(),
    ]);

    const rating = ratingRes.status === "fulfilled" ? ratingRes.value : {};
    if (rating?.company_review_stats?.total_reviews) {
      orgSchema["aggregateRating"] = {
        "@type": "AggregateRating",
        bestRating: 5,
        reviewCount: rating?.company_review_stats?.total_reviews,
        ratingValue: parseFloat(
          parseFloat(rating?.company_review_stats?.average_rating).toFixed(2),
        ),
      };
    }

    const orgInfo = orgRes.status === "fulfilled" ? orgRes.value.data : {};
    if (orgInfo?.items?.length > 0) {
      const fields = orgInfo.items[0]?.fields;
      if (fields?.telephone) {
        const number = formatToInternationalUKPhoneNumber(fields?.telephone);
        orgSchema.telephone = number;
        orgSchema.contactPoint.telephone = number;
      }

      const { imageUrl } = getContentfulImgData(fields?.image);
      const { imageUrl: logoURL } = getContentfulImgData(
        fields?.logo?.fields?.image,
      );

      if (logoURL) orgSchema.logo = logoURL;
      if (imageUrl) orgSchema.image = imageUrl;

      if (fields?.name) {
        orgSchema.name = fields.name;
        orgSchema.vatID = fields?.vatId || orgSchema.vatID;
        orgSchema.email = fields?.email || orgSchema.email;
        orgSchema.sameAs = fields?.sameAs || orgSchema.sameAs;
        orgSchema.description = fields?.description || orgSchema.description;
        orgSchema.contactPoint.email =
          fields?.email || orgSchema.contactPoint.email;
        orgSchema.address.postalCode =
          fields?.postalCode || orgSchema.address.postalCode;
        orgSchema.address.postalCode =
          fields?.addressCountry || orgSchema.address.addressCountry;
        orgSchema.address.streetAddress =
          fields?.streetAddress || orgSchema.address.streetAddress;
        orgSchema.address.addressRegion =
          fields?.addressRegion || orgSchema.address.addressRegion;
        orgSchema.address.addressLocality =
          fields?.addressLocality || orgSchema.address.addressLocality;
      }
    }

    if (!orgSchema.image && icon) {
      const { imageUrl } = getContentfulImgData(icon);
      if (imageUrl) orgSchema.image = imageUrl;
    }
  }
  return orgSchema;
};

export const getWebSiteSchema = () => {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    url: keys.general.websiteUrl,
    potentialAction: {
      "@type": "SearchAction",
      target: `${
        keys.general.websiteUrl + routes.searchResults
      }?q={search_term_string}`,
      "query-input": "required name=search_term_string",
    },
  };
};
